import { signal } from '@preact/signals';

/**
 * Lazy per-book loader for OpenBible.info cross-references (CC-BY 4.0;
 * see About / colophon for attribution). Files live at
 * `/data/cross-refs/<BOOK>.json` and ship as static assets.
 */

export type CrossRef = { to: string; votes: number };

const cache = new Map<string, Record<string, CrossRef[]>>();
const inflight = new Map<string, Promise<Record<string, CrossRef[]>>>();

export function refsForBook(book: string): Record<string, CrossRef[]> | null {
  return cache.get(book.toUpperCase()) ?? null;
}

const loaded = signal<Record<string, number>>({});

export async function loadCrossRefs(book: string): Promise<Record<string, CrossRef[]>> {
  const key = book.toUpperCase();
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = inflight.get(key);
  if (pending) return pending;
  const promise = fetch(`/data/cross-refs/${key}.json`, { credentials: 'omit' })
    .then(async (res) => {
      if (!res.ok) return {} as Record<string, CrossRef[]>;
      const json = (await res.json()) as Record<string, CrossRef[]>;
      cache.set(key, json);
      loaded.value = { ...loaded.peek(), [key]: Object.keys(json).length };
      return json;
    })
    .catch(() => ({}) as Record<string, CrossRef[]>)
    .finally(() => inflight.delete(key));
  inflight.set(key, promise);
  return promise;
}

export function refsForVerse(verseRef: string): CrossRef[] {
  const book = verseRef.split('.')[0];
  if (!book) return [];
  const data = cache.get(book.toUpperCase());
  if (!data) {
    void loadCrossRefs(book);
    return [];
  }
  return data[verseRef] ?? [];
}

/** Track which books have been loaded — UI re-renders when this flips. */
export const crossRefsLoaded = loaded;

/** Pretty-print a BB-coded ref ("MAT.1.21") as "Matthew 1:21". */
export function prettyRef(ref: string): string {
  const [book, ch, v] = ref.split('.');
  if (!book || !ch) return ref;
  const name = BOOK_NAMES[book] ?? book;
  return v ? `${name} ${ch}:${v}` : `${name} ${ch}`;
}

const BOOK_NAMES: Record<string, string> = {
  GEN: 'Genesis', EXO: 'Exodus', LEV: 'Leviticus', NUM: 'Numbers', DEU: 'Deuteronomy',
  JOS: 'Joshua', JDG: 'Judges', RUT: 'Ruth', '1SA': '1 Samuel', '2SA': '2 Samuel',
  '1KI': '1 Kings', '2KI': '2 Kings', '1CH': '1 Chronicles', '2CH': '2 Chronicles',
  EZR: 'Ezra', NEH: 'Nehemiah', EST: 'Esther', JOB: 'Job', PSA: 'Psalms', PRO: 'Proverbs',
  ECC: 'Ecclesiastes', SNG: 'Song of Solomon', ISA: 'Isaiah', JER: 'Jeremiah',
  LAM: 'Lamentations', EZK: 'Ezekiel', DAN: 'Daniel', HOS: 'Hosea', JOL: 'Joel',
  AMO: 'Amos', OBA: 'Obadiah', JON: 'Jonah', MIC: 'Micah', NAM: 'Nahum', HAB: 'Habakkuk',
  ZEP: 'Zephaniah', HAG: 'Haggai', ZEC: 'Zechariah', MAL: 'Malachi',
  MAT: 'Matthew', MRK: 'Mark', LUK: 'Luke', JHN: 'John', ACT: 'Acts',
  ROM: 'Romans', '1CO': '1 Corinthians', '2CO': '2 Corinthians', GAL: 'Galatians',
  EPH: 'Ephesians', PHP: 'Philippians', COL: 'Colossians', '1TH': '1 Thessalonians',
  '2TH': '2 Thessalonians', '1TI': '1 Timothy', '2TI': '2 Timothy', TIT: 'Titus',
  PHM: 'Philemon', HEB: 'Hebrews', JAS: 'James', '1PE': '1 Peter', '2PE': '2 Peter',
  '1JN': '1 John', '2JN': '2 John', '3JN': '3 John', JUD: 'Jude', REV: 'Revelation',
};
